import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Link, useNavigate, useBlocker, getRouteApi } from '@tanstack/react-router'
import { format, startOfDay, isToday } from "date-fns"
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { cn } from "@/lib/utils"
import { Calendar as CalendarIcon, ChevronRight, Bold, Italic, Underline, Type, Link as LinkIcon, Trash2, Info, Loader2 } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ConfirmationModal } from '@/components/ConfirmationModal'
import { LoadingModal } from '@/components/LoadingModal'
import { api, type IngestionTaskStatus } from '@/lib/api'
import { useChatbot, useIngestionTasks, queryKeys } from '@/lib/queries'
import { useQueryClient } from '@tanstack/react-query'

const DIVISION_DOMAINS: Record<string, string> = {
    teacher: 'https://polite-bay-00d6d3400.1.azurestaticapps.net',
    student: 'https://blue-mushroom-026a0a60f.5.azurestaticapps.net',
}

function generateEmbedCode(division: string, id: string): string {
    const domain = DIVISION_DOMAINS[division] ?? DIVISION_DOMAINS.teacher
    const cleanId = id.replace(/^CB/i, '')
    const dataUrl = `${domain}/chatbot/${cleanId}`
    return `<script\n  src="${domain}/embed/chatbot-embed.min.js"\n  data-auto-init="true"\n  data-url="${dataUrl}"\n  data-position="bottom-right"\n></script>`
}

const chatbotSchema = z.object({
    name: z.string().min(1, 'Name is required').max(150, 'Name cannot exceed 150 characters'),
    id: z.string(),
    status: z.boolean(),
    division: z.enum(['teacher', 'student'], { required_error: 'Division is required' }),
    description: z.string().max(500, 'Description cannot exceed 500 characters').nullable().optional(),
    embeddedUrl: z.string().nullable().optional(),
    schedulePublish: z.string().nullable().optional(),
})

type ChatbotForm = z.infer<typeof chatbotSchema>

interface ExtendedChatbotFile {
    id: string
    filename: string
    size: number
    created_at?: string
    file?: File
    status: 'existing' | 'new' | 'replaced'
}

interface FileIngestionInfo {
    status: IngestionTaskStatus
    errorMessage?: string | null
}

const routeApi = getRouteApi('/chatbot')

export function Chatbot() {
    const navigate = useNavigate()
    const search = routeApi.useSearch()
    const isCreate = search.mode === 'create'
    const queryClient = useQueryClient()

    const { data: chatbotData } = useChatbot(!isCreate ? search.id : undefined)
    const { data: ingestionData } = useIngestionTasks(!isCreate ? search.id : undefined)

    const ingestionByFileId: Record<string, FileIngestionInfo> = {}
    if (ingestionData) {
        for (const task of ingestionData.tasks) {
            ingestionByFileId[String(task.document_id)] = {
                status: task.status,
                errorMessage: task.error_message,
            }
        }
    }

    const [showDiscardModal, setShowDiscardModal] = useState(false)
    const [showReplaceModal, setShowReplaceModal] = useState(false)
    const [fileToReplaceId, setFileToReplaceId] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isPreview, setIsPreview] = useState(false)
    const [showToast, setShowToast] = useState(false)
    const [fileError, setFileError] = useState<string | null>(null)
    const descriptionRef = useRef<HTMLTextAreaElement | null>(null)
    const colorInputRef = useRef<HTMLInputElement>(null)

    const [files, setFiles] = useState<ExtendedChatbotFile[]>([])
    const [deletedFileIds, setDeletedFileIds] = useState<string[]>([])
    const [updatingFileId, setUpdatingFileId] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const insertFormat = (prefix: string, suffix: string) => {
        const textarea = descriptionRef.current
        if (!textarea) return

        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const text = textarea.value
        const selectedText = text.substring(start, end)
        const newText = text.substring(0, start) + prefix + selectedText + suffix + text.substring(end)

        form.setValue('description', newText, { shouldDirty: true })

        // Restore selection
        setTimeout(() => {
            textarea.focus()
            textarea.setSelectionRange(start + prefix.length, end + prefix.length)
        }, 0)
    }

    const form = useForm<ChatbotForm>({
        resolver: zodResolver(chatbotSchema),
        defaultValues: {
            name: '',
            id: 'Auto-generated',
            status: false,
            division: 'teacher' as const,
            description: '',
            embeddedUrl: '',
            schedulePublish: '',
        },
    })

    useEffect(() => {
        if (!isCreate && chatbotData) {
            loadFiles(chatbotData.id)
            form.reset({
                name: chatbotData.name,
                id: chatbotData.id,
                status: chatbotData.status === 'Active',
                division: (chatbotData.division === 'student' ? 'student' : 'teacher') as 'teacher' | 'student',
                description: chatbotData.description || '',
                embeddedUrl: chatbotData.embeddedUrl || '',
                schedulePublish: chatbotData.publishDate || '',
            })
        }
    }, [isCreate, chatbotData, form])

    const loadFiles = async (id: string) => {
        try {
            const fileList = await api.getChatbotFiles(id)
            setFiles(fileList.map(f => ({ ...f, status: 'existing' })))
            setDeletedFileIds([])
        } catch (e) {
            console.error("Failed to load files", e)
        }
    }

    const { proceed, reset: resetBlocker, status } = useBlocker({
        condition: (form.formState.isDirty || files.some(f => f.status !== 'existing') || deletedFileIds.length > 0) && !isSubmitting,
    })

    useEffect(() => {
        if (status === 'blocked') {
            setShowDiscardModal(true)
        }
    }, [status])

    const onSubmit = async (data: ChatbotForm) => {
        data.name = data.name.trim().replace(/ +/g, ' ')
        if (typeof data.description === 'string') {
            data.description = data.description.trim().replace(/ +/g, ' ')
        }
        const normalizedDescription = data.description ?? ''
        const normalizedEmbeddedUrl = data.embeddedUrl ?? ''

        if (files.length === 0) {
            setFileError("At least one attachment is required")
            return
        }
        setFileError(null)
        setIsSubmitting(true)
        try {
            const saveResponse = await api.saveChatbot({
                ...data,
                description: normalizedDescription,
                embeddedUrl: normalizedEmbeddedUrl,
                id: isCreate ? undefined : data.id,
                status: data.status ? 'Active' : 'Inactive',
                lastModified: new Date().toISOString()
            })

            const effectiveId = isCreate ? saveResponse.id : data.id
            if (!effectiveId) throw new Error("Could not determine chatbot ID")

            // Process file operations
            for (const id of deletedFileIds) {
                await api.deleteChatbotFile(effectiveId, id)
            }

            for (const file of files) {
                if (file.status === 'new' && file.file) {
                    await api.uploadChatbotFile(effectiveId, file.file)
                } else if (file.status === 'replaced' && file.file) {
                    await api.replaceChatbotFile(effectiveId, file.id, file.file)
                }
            }

            form.reset(data)
            queryClient.invalidateQueries({ queryKey: queryKeys.chatbots })
            if (isCreate) {
                setShowToast(true)
                setTimeout(() => navigate({ to: '/' }), 5000)
            } else {
                setTimeout(() => navigate({ to: '/' }), 0)
            }
        } catch (e) {
            console.error(e)
            alert(`An error occurred during save: ${e instanceof Error ? e.message : "Unknown error"}`)
            setIsSubmitting(false)
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || [])
        if (selectedFiles.length === 0) return
        e.target.value = '' // Reset input
        setFileError(null)

        const validExtensions = ['.pdf', '.doc', '.docx']
        const invalidFiles = selectedFiles.filter(f => {
            const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase()
            return !validExtensions.includes(ext)
        })
        if (invalidFiles.length > 0) {
            setFileError("Invalid file type. Only PDF, DOC, and DOCX are allowed.")
            return
        }

        const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
        const oversizedFiles = selectedFiles.filter(f => f.size > MAX_FILE_SIZE)
        if (oversizedFiles.length > 0) {
            setFileError("File size must not exceed 10MB.")
            return
        }

        if (updatingFileId) {
            const file = selectedFiles[0]
            if (files.some(f => f.id !== updatingFileId && f.filename === file.name)) {
                setFileError(`A file with the same name "${file.name}" already exists.`)
                setUpdatingFileId(null)
                return
            }
            setFiles(prev => prev.map(f => f.id === updatingFileId ? { ...f, filename: file.name, size: file.size, file, status: f.status === 'new' ? 'new' : 'replaced' } : f))
            setUpdatingFileId(null)
        } else {
            const newFiles: ExtendedChatbotFile[] = []
            const duplicateNames: string[] = []
            selectedFiles.forEach(file => {
                if (files.some(f => f.filename === file.name) || newFiles.some(f => f.filename === file.name)) {
                    duplicateNames.push(file.name)
                } else {
                    newFiles.push({ id: `temp-${Date.now()}-${Math.random()}`, filename: file.name, size: file.size, file, status: 'new' })
                }
            })
            if (duplicateNames.length > 0) {
                setFileError(`The following files have duplicate names: ${duplicateNames.join(', ')}`)
            }
            if (newFiles.length > 0) {
                setFiles(prev => [...prev, ...newFiles])
            }
        }
    }

    const handleDeleteFile = (fileId: string) => {
        if (confirm("Are you sure you want to delete this file?")) {
            const file = files.find(f => f.id === fileId)
            if (file && file.status !== 'new') {
                setDeletedFileIds(prev => [...prev, fileId])
            }
            setFiles(prev => prev.filter(f => f.id !== fileId))
        }
    }

    const triggerFileUpload = (fileId: string | null = null) => {
        setUpdatingFileId(fileId)
        setTimeout(() => fileInputRef.current?.click(), 0)
    }

    const handleUpdateClick = (fileId: string) => {
        setFileToReplaceId(fileId)
        setShowReplaceModal(true)
    }

    const handleDiscardConfirm = () => {
        if (status === 'blocked') {
            proceed()
        } else {
            form.reset()
            setFiles([])
            setDeletedFileIds([])
            setTimeout(() => navigate({ to: '/' }), 0)
        }
        setShowDiscardModal(false)
    }

    const handleDiscardCancel = () => {
        setShowDiscardModal(false)
        if (status === 'blocked') {
            resetBlocker()
        }
    }

    return (
        <>
            <div className="p-8 max-w-[1200px]">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                    <Link to="/" className="hover:text-gray-900">Chatbot Management</Link>
                    <ChevronRight className="w-4 h-4" />
                    <span className="font-bold text-gray-900">{isCreate ? 'Create New Chatbot' : (form.watch('name') || 'Edit Chatbot')}</span>
                </div>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                        {/* Header */}
                        <div className="flex justify-between items-center">
                            <h1 className="text-3xl font-bold text-gray-900">{isCreate ? 'Create New Chatbot' : 'Edit Chatbot'}</h1>
                            <div className="flex gap-3">
                                <Button type="button" variant="outline" onClick={() => setShowDiscardModal(true)} disabled={isSubmitting}>Discard</Button>
                                <Button type="submit" className="bg-[#D91F27] hover:bg-[#b81a21] text-white min-w-[80px]" disabled={isSubmitting}>
                                    Save
                                </Button>
                            </div>
                        </div>

                        {/* Row 1: Name and ID */}
                        <div className="grid grid-cols-2 gap-8 items-start">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-900 font-semibold">Chatbot Name<span className="text-[#D91F27]">*</span></FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Enter chatbot name" maxLength={150} />
                                        </FormControl>
                                        <div className="flex justify-end mt-1">
                                            <span className="text-xs text-gray-500">{field.value?.length || 0}/150</span>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-900 font-semibold">ID</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled className="bg-gray-50 text-gray-400" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Status and Schedule Publish */}
                        <div className="grid grid-cols-2 gap-8 items-start">
                            <FormField
                                control={form.control}
                                name="status"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-gray-900 font-semibold block mb-2">Status</FormLabel>
                                        <div className="flex items-center gap-3">
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(e) => {
                                                        field.onChange(e)
                                                        if (e.target.checked) {
                                                            form.setValue("schedulePublish", new Date().toISOString(), { shouldDirty: true })
                                                        } else {
                                                            form.setValue("schedulePublish", "", { shouldDirty: true })
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                            <span className="text-sm font-medium">{field.value ? "Active" : "Inactive"}</span>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            {!form.watch("status") && (
                                <FormField
                                    control={form.control}
                                    name="schedulePublish"
                                    render={({ field }) => {
                                        const isTodaySelected = field.value ? isToday(new Date(field.value)) : false;
                                        const now = new Date();
                                        const currH = now.getHours();
                                        const currM = now.getMinutes();
                                        const currentAMPM = field.value ? format(new Date(field.value), "a") : "AM";
                                        const selectedH24 = field.value ? new Date(field.value).getHours() : 0;

                                        return (
                                            <FormItem>
                                                <FormLabel className="text-gray-900 font-semibold">Schedule Publish</FormLabel>
                                                <Popover onOpenChange={(open) => {
                                                    if (open && !field.value) {
                                                        field.onChange(new Date().toISOString());
                                                    }
                                                }}>
                                                    <PopoverTrigger asChild>
                                                        <FormControl>
                                                            <Button
                                                                variant={"outline"}
                                                                disabled={form.watch("status")}
                                                                className={cn(
                                                                    "w-full justify-between text-left font-normal border-input shadow-xs h-9 bg-white",
                                                                    !field.value && "text-muted-foreground"
                                                                )}
                                                            >
                                                                {field.value ? format(new Date(field.value), "MM/dd/yyyy hh:mm a") : <span>MM/DD/YYYY hh:mm a</span>}
                                                                <CalendarIcon className="h-4 w-4 opacity-50" />
                                                            </Button>
                                                        </FormControl>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0" align="start">
                                                        <Calendar
                                                            mode="single"
                                                            selected={field.value ? new Date(field.value) : undefined}
                                                            disabled={(date) => date < startOfDay(new Date())}
                                                            onSelect={(date) => {
                                                                if (date) {
                                                                    const nowTime = new Date();
                                                                    const currentTime = field.value ? new Date(field.value) : new Date();
                                                                    date.setHours(currentTime.getHours());
                                                                    date.setMinutes(currentTime.getMinutes());
                                                                    if (isToday(date) && date < nowTime) {
                                                                        date.setHours(nowTime.getHours());
                                                                        date.setMinutes(nowTime.getMinutes());
                                                                    }
                                                                    field.onChange(date.toISOString());
                                                                } else {
                                                                    field.onChange("");
                                                                }
                                                            }}
                                                            initialFocus
                                                        />
                                                        <div className="p-3 border-t border-border flex items-center justify-between gap-2">
                                                            <span className="text-sm font-medium text-gray-700">Time</span>
                                                            <div className="flex items-center gap-1">
                                                                <Select
                                                                    value={field.value ? format(new Date(field.value), "hh") : "12"}
                                                                    onValueChange={(v) => {
                                                                        const newDate = field.value ? new Date(field.value) : new Date();
                                                                        const isPM = newDate.getHours() >= 12;
                                                                        const h12 = parseInt(v, 10);
                                                                        newDate.setHours((h12 % 12) + (isPM ? 12 : 0));
                                                                        if (isTodaySelected && newDate < now) {
                                                                            newDate.setMinutes(now.getMinutes());
                                                                        }
                                                                        field.onChange(newDate.toISOString());
                                                                    }}
                                                                >
                                                                    <SelectTrigger className="w-[60px] h-8 px-2"><SelectValue /></SelectTrigger>
                                                                    <SelectContent>
                                                                        {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map(h => {
                                                                            const hNum = parseInt(h, 10);
                                                                            const h24 = (hNum % 12) + (currentAMPM === "PM" ? 12 : 0);
                                                                            const isDisabled = isTodaySelected && h24 < currH;
                                                                            return (
                                                                                <SelectItem key={h} value={h} disabled={isDisabled}>{h}</SelectItem>
                                                                            );
                                                                        })}
                                                                    </SelectContent>
                                                                </Select>
                                                                <span className="text-sm font-medium">:</span>
                                                                <Select
                                                                    value={field.value ? format(new Date(field.value), "mm") : "00"}
                                                                    onValueChange={(v) => {
                                                                        const newDate = field.value ? new Date(field.value) : new Date();
                                                                        newDate.setMinutes(parseInt(v, 10));
                                                                        field.onChange(newDate.toISOString());
                                                                    }}
                                                                >
                                                                    <SelectTrigger className="w-[60px] h-8 px-2"><SelectValue /></SelectTrigger>
                                                                    <SelectContent className="max-h-[200px]">
                                                                        {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map(m => {
                                                                            const isDisabled = isTodaySelected && selectedH24 === currH && parseInt(m, 10) < currM;
                                                                            return (
                                                                                <SelectItem key={m} value={m} disabled={isDisabled}>{m}</SelectItem>
                                                                            );
                                                                        })}
                                                                    </SelectContent>
                                                                </Select>
                                                                <Select
                                                                    value={field.value ? format(new Date(field.value), "a") : "AM"}
                                                                    onValueChange={(v) => {
                                                                        const newDate = field.value ? new Date(field.value) : new Date();
                                                                        let h24 = newDate.getHours();
                                                                        const isPM = h24 >= 12;
                                                                        if (v === 'PM' && !isPM) newDate.setHours(h24 + 12);
                                                                        if (v === 'AM' && isPM) newDate.setHours(h24 - 12);
                                                                        if (isTodaySelected && newDate < now) {
                                                                            newDate.setHours(now.getHours());
                                                                            newDate.setMinutes(now.getMinutes());
                                                                        }
                                                                        field.onChange(newDate.toISOString());
                                                                    }}
                                                                >
                                                                    <SelectTrigger className="w-[65px] h-8 px-2"><SelectValue /></SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="AM" disabled={isTodaySelected && currH >= 12}>AM</SelectItem>
                                                                        <SelectItem value="PM">PM</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                                <FormMessage />
                                            </FormItem>
                                        )
                                    }}
                                />
                            )}
                        </div>

                        {/* Description */}
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <div className="flex justify-between items-center">
                                        <FormLabel className="text-gray-900 font-semibold">Description</FormLabel>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500">Preview</span>
                                            <Switch checked={isPreview} onChange={(e) => setIsPreview(e.target.checked)} />
                                        </div>
                                    </div>
                                    {isPreview ? (
                                        <div className="min-h-[245px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm [&_b]:font-bold [&_i]:italic [&_u]:underline [&_a]:text-blue-600 [&_a]:underline whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: field.value || '' }} />
                                    ) : (
                                        <div className="border border-input rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                            <div className="relative">
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="Enter Description"
                                                        className="min-h-[200px] resize-none border-0 focus-visible:ring-0 shadow-none"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                        maxLength={500}
                                                        ref={(e) => {
                                                            field.ref(e)
                                                            descriptionRef.current = e
                                                        }}
                                                    />
                                                </FormControl>
                                            </div>
                                            <div className="flex justify-between items-center p-2 border-t border-input text-gray-500">
                                                <div className="flex gap-4">
                                                    <Bold size={16} className="cursor-pointer hover:text-gray-900" onClick={() => insertFormat('<b>', '</b>')} />
                                                    <Italic size={16} className="cursor-pointer hover:text-gray-900" onClick={() => insertFormat('<i>', '</i>')} />
                                                    <Underline size={16} className="cursor-pointer hover:text-gray-900" onClick={() => insertFormat('<u>', '</u>')} />
                                                    <div className="relative">
                                                        <Type size={16} className="cursor-pointer hover:text-gray-900" onClick={() => colorInputRef.current?.click()} />
                                                        <input
                                                            type="color"
                                                            ref={colorInputRef}
                                                            className="absolute opacity-0 w-0 h-0"
                                                            onChange={(e) => insertFormat(`<span style="color: ${e.target.value}">`, '</span>')}
                                                        />
                                                    </div>
                                                    <LinkIcon
                                                        size={16}
                                                        className="cursor-pointer hover:text-gray-900"
                                                        onClick={() => {
                                                            const input = prompt('Enter URL:')
                                                            if (input) {
                                                                const trimmed = input.trim()
                                                                const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
                                                                try {
                                                                    const parsed = new URL(withProtocol)
                                                                    if (!/^https?:$/i.test(parsed.protocol)) {
                                                                        return
                                                                    }
                                                                    const safeHref = parsed.toString().replace(/"/g, '&quot;')
                                                                    insertFormat(`<a href="${safeHref}" target="_blank" rel="noopener noreferrer">`, '</a>')
                                                                } catch {
                                                                    // Invalid URL; do not insert a link
                                                                    return
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {field.value?.length || 0}/500
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Attachments */}
                        <div className="space-y-4">
                            <FormLabel className="text-gray-900 font-semibold">Attachments<span className="text-[#D91F27]">*</span></FormLabel>
                            <div>
                                <Button type="button" variant="outline" className="font-semibold text-gray-700" onClick={() => triggerFileUpload(null)}>
                                    Upload
                                </Button>
                            </div>
                            <p className="text-xs text-gray-500">Supported formats: DOC, DOCX, and PDF.</p>
                            {fileError && <p className="text-sm font-medium text-destructive">{fileError}</p>}
                            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept=".doc,.docx,.pdf" multiple={!updatingFileId} />

                            <div className="space-y-2 mt-4">
                                {files.length > 0 ? (
                                    files.map((file) => {
                                        const ingestion = ingestionByFileId[file.id]
                                        return (
                                            <div key={file.id} className="flex items-center justify-between p-3 border rounded-md bg-white">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2">
                                                        {file.status === 'existing' ? (
                                                            <a
                                                                href={`${process.env.BUN_PUBLIC_API_URL}/admin/chatbots/${form.getValues('id')}/files/${file.id}/download`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-sm font-medium text-blue-600 hover:underline"
                                                            >
                                                                {file.filename}
                                                            </a>
                                                        ) : (
                                                            <span className="text-sm font-medium text-gray-700">{file.filename}</span>
                                                        )}
                                                        {ingestion && (
                                                            <span className={cn(
                                                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                                                                (ingestion.status === 'COMPLETED' || ingestion.status === 'NO_TASK') && "bg-green-100 text-green-700",
                                                                ingestion.status === 'FAILED' && "bg-red-100 text-red-700",
                                                                ingestion.status === 'PENDING' && "bg-yellow-100 text-yellow-700",
                                                                ingestion.status === 'PROCESSING' && "bg-blue-100 text-blue-700",
                                                            )} title={ingestion.status === 'FAILED' && ingestion.errorMessage ? ingestion.errorMessage : undefined}>
                                                                {(ingestion.status === 'PENDING' || ingestion.status === 'PROCESSING') && (
                                                                    <Loader2 size={12} className="animate-spin" />
                                                                )}
                                                                {(ingestion.status === 'COMPLETED' || ingestion.status === 'NO_TASK') ? 'Ingested' : ingestion.status === 'FAILED' ? 'Failed' : ingestion.status === 'PENDING' ? 'Pending' : 'Processing'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {ingestion?.status === 'FAILED' && ingestion.errorMessage && (
                                                        <span className="text-xs text-red-600">{ingestion.errorMessage}</span>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 border-[#D91F27] text-[#D91F27] hover:bg-red-50 hover:text-[#D91F27]"
                                                        title="Replace this document"
                                                        onClick={() => handleUpdateClick(file.id)}
                                                        disabled={ingestionByFileId[file.id]?.status === 'PENDING' || ingestionByFileId[file.id]?.status === 'PROCESSING'}
                                                    >
                                                        Update
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 px-2 border-[#D91F27] text-[#D91F27] hover:bg-red-50 hover:text-[#D91F27]"
                                                        title="Delete this document"
                                                        onClick={() => handleDeleteFile(file.id)}
                                                        disabled={ingestionByFileId[file.id]?.status === 'PENDING' || ingestionByFileId[file.id]?.status === 'PROCESSING'}
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                                        <Info size={48} strokeWidth={1} className="mb-2" />
                                        <span className="text-sm">No files yet</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Embed Code */}
                        {!isCreate && (
                            <div className="space-y-2">
                                <FormLabel className="text-gray-900 font-semibold block">Embed Code</FormLabel>
                                <div className="relative">
                                    <pre className="bg-gray-50 border border-input rounded-md px-4 py-3 text-sm font-mono text-gray-700 whitespace-pre overflow-x-auto pr-20">
                                        {generateEmbedCode(form.watch('division'), form.watch('id'))}
                                    </pre>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="absolute top-2 right-2 h-7 text-xs px-2"
                                        onClick={() => navigator.clipboard.writeText(generateEmbedCode(form.watch('division'), form.watch('id')))}
                                    >
                                        Copy
                                    </Button>
                                </div>
                            </div>
                        )}

                    </form>
                </Form>
            </div>
            {showToast && (
                <div className="fixed bottom-4 right-4 z-[110] bg-[#198754] text-white px-6 py-3 rounded-md shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-300">
                    Chatbot is created successfully
                </div>
            )}
            <ConfirmationModal
                isOpen={showReplaceModal}
                onClose={() => setShowReplaceModal(false)}
                onConfirm={() => {
                    if (fileToReplaceId) triggerFileUpload(fileToReplaceId)
                    setShowReplaceModal(false)
                }}
                title="Replace this file?"
                description="This action can't be undone."
                confirmText="Replace"
                confirmVariant="destructive"
            />
            <ConfirmationModal
                isOpen={showDiscardModal}
                onClose={handleDiscardCancel}
                onConfirm={handleDiscardConfirm}
                title="Discard changes?"
                description="Your changes won't be saved."
                confirmText="Discard"
                confirmVariant="destructive"
            />
            <LoadingModal isOpen={isSubmitting} message="Saving..." />
        </>
    )
}
